import request from '@medlinker/apitool/request';
import * as qiankun from 'qiankun';
import subappConfig from '../../sub-apps.json';
import { commonStorage } from '../shared/storage';
import pkgInfo from '../../package.json';

const configItem = commonStorage.createStorageItem<any>(
  '__appconfig__',
  Number(pkgInfo.version.replace(/\./g, ''))
);

class AppManager {
  private promise: Promise<any>;
  private retryCount = 0;
  private resolve: ((value: any) => void) | undefined;

  constructor() {
    qiankun.addGlobalUncaughtErrorHandler(event => console.log(event));

    this.tryGetConfig();

    const cache = this.getCache();

    this.promise = new Promise(resolve => {
      this.resolve = resolve;

      if (cache) {
        this.resolve(cache);
      }
    });
  }

  /**
   * 获取子应用配置列表
   * @returns
   */
  async getMicroAppConfig() {
    return this.promise;
  }

  /**
   * 获取缓存
   * @private
   * @returns
   */
  private getCache = () => configItem.load();

  /**
   * 拉取远端配置
   * @private
   * @returns
   */
  private getRemoteConfig = async () => {
    const swimlane = process.env.MED_SWIMLANE;
    const t = Math.floor(Date.now() / 1000);

    // 预发特殊处理，只拉取预发配置
    if (swimlane === 'preprod') {
      const { data } = await request.get<any, any>(
        process.env.MED_CONFIG_URL + `appConfig-preprod.json?t=${t}`
      );

      return data;
    }

    // 泳道需要合并应用配置
    const { data } = await request.get<any, any>(
      process.env.MED_CONFIG_URL + `appConfig.json?t=${t}`
    );

    if (swimlane) {
      const { data: swimConfig } = await request.get<any, any>(
        process.env.MED_CONFIG_URL +
          `appConfig${swimlane ? `-${swimlane}` : ''}.json?t=${t}`
      );

      const sApps = [...swimConfig.apps];

      data.apps.forEach((item: any, index: number) => {
        sApps.some((sitem: any, sindex: number) => {
          if (sitem.name === item.name) {
            data.apps.splice(index, 1, sitem);
            sApps.splice(sindex, 1);

            return true;
          }

          return false;
        });
      });

      data.apps = [...data.apps, ...sApps];
    }

    return data;
  };

  /**
   * 初始化
   * @returns
   */
  private async tryGetConfig() {
    try {
      const remoteConfig = (await this.getRemoteConfig()) as any;

      let appConfig: any;

      if (process.env.NODE_ENV === 'production') {
        appConfig = remoteConfig;
      } else {
        const apps = subappConfig.map((app: any) => {
          if (app.independent) {
            return {
              name: app.name,
              activeRule: app.activeRule,
              entry: app.entry,
            };
          }

          return {
            name: app.name,
            activeRule: app.activeRule,
            entry: {
              scripts: [app.devEntry],
            },
          };
        });

        // 开发时，未启动的应用，降级使用远程环境的包。
        remoteConfig.apps.forEach((item: any) => {
          if (apps.find(app => app.name === item.name)) {
            return;
          }

          apps.push({ ...item, __remote: true });
        });

        appConfig = { apps };
      }

      /**
       * 设置缓存
       */
      configItem.save({ value: appConfig, maxAge: 1000 * 60 * 5 });

      this.resolve && this.resolve(appConfig);

      setTimeout(() => {
        this.tryGetConfig();
      }, 60 * 1000);
    } catch (e) {
      this.retryCount < 10 && this.retryCount++;

      console.warn(`获取配置文件失败..., ${this.retryCount}秒后重试`);

      // 10次后再上报
      if (this.retryCount === 10) {
        if (
          typeof Sentry !== 'undefined' &&
          process.env.NODE_ENV === 'production'
        ) {
          Sentry.captureException('获取应用配置列表失败');
        }
      }

      setTimeout(() => {
        this.tryGetConfig();
      }, this.retryCount * 1000);
    }
  }
}

export default new AppManager();
